import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';

/**
 * Renders a DOM element into a multi-page A4 PDF.
 * Strategy: capture the element as one tall canvas, then slice it onto
 * successive A4 pages so long contracts don't get cropped.
 */
export const generatePdfBlob = async (elementId: string): Promise<Blob | null> => {
  const element = document.getElementById(elementId);
  if (!element) return null;

  try {
    const canvas = await html2canvas(element, {
      scale: 2,
      logging: false,
      useCORS: true,
      backgroundColor: '#ffffff',
      windowWidth: 1200,
    });

    const pdf = new jsPDF('p', 'mm', 'a4');
    const pdfWidthMm = pdf.internal.pageSize.getWidth(); // 210
    const pdfHeightMm = pdf.internal.pageSize.getHeight(); // 297

    // How many canvas pixels fit on one PDF page (preserving width-fit scaling)
    const pxPerMm = canvas.width / pdfWidthMm;
    const pageHeightPx = Math.floor(pdfHeightMm * pxPerMm);

    let renderedPx = 0;
    let pageIndex = 0;

    while (renderedPx < canvas.height) {
      const sliceHeightPx = Math.min(pageHeightPx, canvas.height - renderedPx);

      // Draw the slice onto a temporary canvas, then export to JPEG
      const sliceCanvas = document.createElement('canvas');
      sliceCanvas.width = canvas.width;
      sliceCanvas.height = sliceHeightPx;
      const ctx = sliceCanvas.getContext('2d');
      if (!ctx) break;
      // White background in case the slice has transparent regions
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, sliceCanvas.width, sliceCanvas.height);
      ctx.drawImage(
        canvas,
        0,
        renderedPx,
        canvas.width,
        sliceHeightPx,
        0,
        0,
        canvas.width,
        sliceHeightPx,
      );

      const imgData = sliceCanvas.toDataURL('image/jpeg', 0.85);
      const sliceHeightMm = sliceHeightPx / pxPerMm;

      if (pageIndex > 0) pdf.addPage();
      pdf.addImage(imgData, 'JPEG', 0, 0, pdfWidthMm, sliceHeightMm);

      renderedPx += sliceHeightPx;
      pageIndex += 1;
    }

    return pdf.output('blob');
  } catch (error) {
    console.error('PDF generation failed:', error);
    return null;
  }
};

export const blobToBase64 = (blob: Blob): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result as string;
      resolve(result.split(',')[1]);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};
